import React, { useState } from "react"
import { componentStyles, RadioSelect } from "@mallardbay/lib-react-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUser } from "@fortawesome/free-solid-svg-icons/faUser"
import { faUsers } from "@fortawesome/free-solid-svg-icons/faUsers"

import Divider from "~components/shared/divider"
import GroupPaySummary from "~components/pages/book/group-pay-summary"
import type { QuoteFieldsFragment } from "~graphql/generated/graphql"
import COPY from "~config/copy-constants"
import { useBookTrip } from "~components/pages/book/book-trip-provider"
import {
    Text,
    TextLarge,
} from "~components/shared/todo-lib-react-components/text"
import GroupPaySelectOption, {
    GroupPayOptionValues,
} from "~components/pages/book/group-pay-select-option"

export interface Props {
    readonly quote: QuoteFieldsFragment
}

export default function GroupPay({ quote }: Props) {
    const [hostSlots, setHostSlots] = useState(1)
    const [selectedOption, setSelectedOption] = useState<GroupPayOptionValues>(
        quote.payment_group ? GroupPayOptionValues.Yes : GroupPayOptionValues.No
    )

    const styles = useStyles()
    const { enableGroupPay, disableGroupPay } = useBookTrip()

    const handleSelectorChange = (value: GroupPayOptionValues) => {
        if (value === GroupPayOptionValues.Yes) {
            void enableGroupPay({ variables: { quoteId: quote.id, hostSlots } })
        } else {
            setHostSlots(1)
            void disableGroupPay()
        }

        setSelectedOption(value)
    }

    const handleHostSlotsChange = (newHostSlots: number) => {
        setHostSlots(newHostSlots)
        void enableGroupPay({
            variables: { quoteId: quote.id, hostSlots: newHostSlots },
        })
    }

    // Hide group pay UI if there aren't enough slots.
    if (quote.items.length < 2) return null

    return (
        <div>
            <TextLarge>{COPY.GROUP_PAY_TITLE}</TextLarge>
            <Text style={styles.subtitle}>{COPY.GROUP_PAY_SUBTITLE}</Text>
            <RadioSelect
                value={selectedOption}
                onChange={handleSelectorChange}
                options={getGroupPaySelectorItems()}
                getOptionValue={(option) => option.value}
                renderOption={(option) => (
                    <GroupPaySelectOption option={option} />
                )}
            />
            <GroupPaySummary
                quote={quote}
                hostSlots={hostSlots}
                handleHostSlotsChange={handleHostSlotsChange}
            />
            <Divider margin />
        </div>
    )
}

function useStyles() {
    return componentStyles({
        subtitle: {
            marginTop: 1,
            marginBottom: 3,
        },
    })
}

function getGroupPaySelectorItems() {
    return [
        {
            value: GroupPayOptionValues.No,
            label: COPY.NO,
            icon: <FontAwesomeIcon icon={faUser} />,
            description: COPY.PAY_ENTIRE_COST,
        },
        {
            value: GroupPayOptionValues.Yes,
            label: COPY.YES,
            icon: <FontAwesomeIcon icon={faUsers} />,
            description: COPY.INVITE_OTHERS_TO_SHARE_COST,
        },
    ]
}
